/**
 * @file LeadsTableSDRVirtual.jsx
 * @description Componente de tabela para exibição de leads no sistema SDR Virtual
 * 
 * Este componente renderiza uma tabela com informações de leads utilizando
 * componentes do Material UI. A tabela permite a interação com os IDs de WhatsApp,
 * que são clicáveis para alguma ação definida no componente pai.
 */

import React from 'react';
import {
  TableContainer, Paper, Table, TableHead,
  TableRow, TableCell, TableBody, Typography, Chip, Box, Button,
  useMediaQuery, useTheme, IconButton, Tooltip
} from "@mui/material";

import TableChartIcon from '@mui/icons-material/TableChart';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import MUIKanbanBoard from './KanbanBoard';




/**
 * Componente de tabela para exibição de leads
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Array} props.leads - Array de objetos contendo os dados dos leads a serem exibidos
 * @param {Function} props.handleWhatsAppIdClick - Função para tratar o clique no ID do WhatsApp
 * @param {Function} props.formatDate - Função para formatar datas para exibição
 * @returns {JSX.Element} Tabela com dados dos leads
 */
const LeadsTableSDRVirtual = ({
  leads,
  handleWhatsAppIdClick,
  formatDate,
  setForceTokenExpired,
  viewMode = 'table',
  setViewMode,
  dateFilter,
  startDate,
  endDate,
}) => {

  const [visibleLeads, setVisibleLeads] = React.useState(12); // Estado para controlar quantos leads são visíveis
  const [loadingAll, setLoadingAll] = React.useState(false); // ⬅️ Novo estado para loading "Ver todos"
  const [copiedLeadId, setCopiedLeadId] = React.useState(null);
  const copiedTimeoutRef = React.useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const copyToClipboard = async (text) => {
    if (!text) return false;
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        return true;
      } catch {
        return false;
      }
    }
  };

  const handleWhatsAppContextMenu = async (event, lead) => {
    event.preventDefault();
    event.stopPropagation();
    const success = await copyToClipboard(lead?.whatsAppId);
    if (!success) return;

    setCopiedLeadId(lead?.idLead);
    if (copiedTimeoutRef.current) {
      clearTimeout(copiedTimeoutRef.current);
    }
    copiedTimeoutRef.current = setTimeout(() => {
      setCopiedLeadId(null);
    }, 1200);
  };

  React.useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  // Função para determinar a cor do chip baseado na situação
  const getSituacaoColor = (situacao) => {
    switch (situacao) {
      case 'Qualificado':
        return '#4CAF50'; // verde
      case 'Em Atendimento':
        return '#FF9800'; // laranja
      case 'Descartado':
        return '#F44336'; // vermelho
      case 'Não é WhatsApp':
        return '#9C27B0'; // roxo
      case 'Retirado da etapa IA':
        return '#2196F3'; // azul
      case 'Outros Sistemas':
        return '#3949ab'; // indigo
      case 'Novo Lead':
        return '#F3E5F5'; // lilás clarinho
      default:
        return '#9E9E9E'; // cinza
    }
  };



  // Função para garantir que pelo menos os 2 primeiros nomes apareçam
  const formatNome = (nomeCompleto) => {
    if (!nomeCompleto) return '';

    const nomes = nomeCompleto.trim().split(' ');
    if (nomes.length >= 2) {
      return `${nomes[0]} ${nomes[1]}`;
    }
    return nomeCompleto;
  };


  // ⬅️ Função para lidar com "Ver todos" com loading
  const handleViewAll = async () => {
    setLoadingAll(true);

    // Simula carregamento de todos os leads (substitua pela sua lógica real)
    await new Promise(resolve => setTimeout(resolve, 2000)); // 2 segundos de loading

    setVisibleLeads(leads.length);
    setLoadingAll(false);
  };

  return (
    <>
      {/* Header da tabela */}
      <Box sx={{ mb: 1, overflowX: 'visible' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Botão Kanban */}
          <Tooltip title="Visualizar em Quadro">
            <IconButton
              size="small"
              onClick={() => setViewMode && setViewMode('kanban')}
              color={viewMode === 'kanban' ? 'primary' : 'default'}
            >
              <TableChartIcon fontSize="large" />
            </IconButton>
          </Tooltip>
          {/* Botão Tabela */}
          <Tooltip title="Visualizar em Lista">
            <IconButton
              size="small"
              onClick={() => setViewMode && setViewMode('table')}
              color={viewMode === 'table' ? 'primary' : 'default'}
            >
              <ViewKanbanIcon fontSize="large" sx={{ transform: 'rotate(270deg)' }} />
            </IconButton>
          </Tooltip>

          <Chip
            label={`${leads.length} ${leads.length === 1 ? 'Lead' : 'Leads'}`}
            onClick={() => { }}
            clickable={false}
            sx={{
              bgcolor: 'rgba(30, 24, 24, 0.2)',
              color: 'white',
              fontWeight: 'medium',
              fontSize: '0.875rem',
            }}
          />
        </Box>
      </Box>

      {viewMode === 'table' ? (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            bgcolor: 'background.paper',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 2px 12px rgba(0,0,0,0.28)'
                : '0 2px 12px rgba(0,0,0,0.08)',
            border: '1px solid',
            borderColor: 'divider',
            overflowY: 'hidden',
            overflowX: isMobile ? 'auto' : 'hidden',
            mb: 3,
            minHeight: loadingAll ? '400px' : 'auto', // ⬅️ Altura mínima durante loading
          }}
          aria-label="Tabela com lista de leads filtrados"
        >
          <Table sx={{ minWidth: 800 }}>
            {/* Cabeçalho da tabela com gradiente sutil */}
            <TableHead>
              <TableRow
                sx={{
                  background: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.16) 0%, rgba(118, 75, 162, 0.18) 100%)'
                      : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  '& .MuiTableCell-head': {
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    color: 'text.primary',
                    letterSpacing: '0.5px',
                    py: 2,
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                  }
                }}
              >
                <TableCell>ID-Lead</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>WhatsAppId</TableCell>
                <TableCell>Situação Interna</TableCell>
                <TableCell>Empreendimento</TableCell>
                <TableCell>Motivo Descarte</TableCell>
                <TableCell>Data Cadastro</TableCell>
              </TableRow>
            </TableHead>

            {/* Corpo da tabela com linhas alternadas e hover effects */}
            <TableBody>
              {leads.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{
                      textAlign: 'center',
                      py: 6,
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="body1">
                      Nenhum lead encontrado com os filtros aplicados
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                /* 
                  Mapeamento do array de dados limitado pela quantidade visível (visibleLeads)
                  Cada item do array é passado para renderizar uma linha da tabela
                */
                leads.slice(0, visibleLeads).map((lead, index) => (
                  <TableRow
                    key={lead.idLead}
                    sx={{
                      backgroundColor: (theme) => {
                        if (theme.palette.mode === 'dark') {
                          return index % 2 === 0
                            ? 'rgba(255,255,255,0.025)'
                            : 'rgba(255,255,255,0.045)';
                        }
                        return index % 2 === 0 ? '#ffffff' : '#f8f9fa';
                      },
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(102, 126, 234, 0.14)'
                            : '#e3f2fd',
                        transform: 'scale(1.001)',
                        boxShadow: (theme) =>
                          theme.palette.mode === 'dark'
                            ? '0 2px 8px rgba(0,0,0,0.32)'
                            : '0 2px 8px rgba(0,0,0,0.1)',
                      },
                      transition: 'all 0.2s ease-in-out',
                      '& .MuiTableCell-root': {
                        py: 2,
                        fontSize: '0.875rem',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        color: 'text.primary',
                      }
                    }}
                  >
                    <TableCell sx={{ width: '150px' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'bold',
                          color: 'text.primary',
                        }}
                      >
                        #{lead.idLead}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ width: '150px' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.primary',
                          maxWidth: '150px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={lead.nome}
                      >
                        {formatNome(lead.nome)}
                      </Typography>
                    </TableCell>

                    {/* 
                      Campo WhatsAppId é clicável e destacado - mais compacto
                      Quando clicado, chama a função handleWhatsAppIdClick passando o lead atual
                    */}
                    <TableCell sx={{ width: '150px' }}>
                      {(lead.situacaoInterna === 'Não é WhatsApp' || lead.idSituacaoInterna === 4 || !lead.whatsAppId) ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.disabled',
                            fontStyle: 'italic',
                          }}
                        >
                          -
                        </Typography>
                      ) : (
                        <Tooltip
                          title="Copiado"
                          open={copiedLeadId === lead.idLead}
                          placement="top"
                          arrow
                          disableFocusListener
                          disableHoverListener
                          disableTouchListener
                        >
                          <Box
                            onClick={() => handleWhatsAppIdClick(lead)}
                            onContextMenu={(event) => handleWhatsAppContextMenu(event, lead)}
                            sx={{
                              cursor: 'pointer',
                              display: 'inline-block',
                              bgcolor: 'rgba(102, 126, 234, 0.1)',
                              color: 'primary.main',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 2,
                              fontSize: '0.8rem',
                              fontWeight: '500',
                              border: '1px solid rgba(102, 126, 234, 0.2)',
                              transition: 'all 0.2s ease-in-out',
                              maxWidth: '110px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              '&:hover': {
                                bgcolor: 'rgba(102, 126, 234, 0.2)',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                              },
                            }}
                          >
                            {lead.whatsAppId}
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>

                    <TableCell sx={{ width: '150px' }}>
                      <Chip
                        label={lead.situacaoInterna}
                        size="small"
                        onClick={() => { }} // Previne qualquer ação
                        clickable={false}
                        sx={{
                          fontWeight: '500',
                          fontSize: '0.75rem',
                          borderRadius: 2,
                          cursor: 'default',
                          maxWidth: '130px',
                          backgroundColor: getSituacaoColor(lead.situacaoInterna),
                          color: '#FFFFFF', // cor do texto (ajuste se quiser outra)
                          // Se quiser texto escuro só para "Novo Lead":
                          ...(lead.situacaoInterna === 'Novo Lead' && {
                            color: '#4A148C',
                          }),
                        }}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '150px' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.primary',
                          maxWidth: '150px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={lead.nomeEmpreendimento}
                      >
                        {lead.nomeEmpreendimento}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ width: '150px' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: lead.motivoDescarte ? 'error.main' : 'text.disabled',
                          fontStyle: lead.motivoDescarte ? 'normal' : 'italic',
                          maxWidth: '140px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={lead.motivoDescarte || 'Não informado'}
                      >
                        {lead.motivoDescarte || 'Sem Motivo'}
                      </Typography>
                    </TableCell>

                    {/* Formata a data usando a função formatDate recebida como prop */}
                    <TableCell sx={{ width: '150px' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.8rem',
                        }}
                      >
                        {formatDate(lead.dataHoraCadastroAtualizado)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box>
          <MUIKanbanBoard
            dateFilter={dateFilter}
            startDate={startDate}
            endDate={endDate}
            setForceTokenExpired={setForceTokenExpired}
            handleWhatsAppIdClick={handleWhatsAppIdClick}
            leads={leads}           // usa os mesmos leads filtrados da gestão
          />
        </Box>
      )}


      {/* Botões de Paginação - só aparece se há mais de 12 itens */}
      {leads.length > 12 && !loadingAll && viewMode === 'table' && (
        <Box
          sx={{ textAlign: 'center', p: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>

            {/*Botão "Ver mais" versão Mobile*/}
            {(visibleLeads < leads.length && isMobile) && (
              <Button
                onClick={() => setVisibleLeads(visibleLeads + 10)}
                variant="outlined"
                size="small"
                sx={{
                  px: 0.5, py: 0.5,
                  border: '1px solid #667eea',
                  borderRadius: '100%',
                  background: 'rgba(102, 126, 234, 0.08)',
                  color: '#667eea',
                  fontWeight: 1000,
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  minWidth: '40px',
                  minHeight: '40px',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
                    background: 'rgba(102, 126, 234, 0.12)',
                  }
                }}
              >
                +
              </Button>
            )}

            {/*Botão "Ver mais" versão Desktop*/}
            {(visibleLeads < leads.length && !isMobile) && (
              <Button
                onClick={() => setVisibleLeads(visibleLeads + 10)}
                variant="outlined"
                size="small"
                sx={{
                  px: 2, py: 0.5,
                  border: '1px solid #667eea',
                  borderRadius: '16px',
                  background: 'rgba(102, 126, 234, 0.08)',
                  color: '#667eea',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  minWidth: '80px',
                  minHeight: '28px',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
                    background: 'rgba(102, 126, 234, 0.12)',
                  }
                }}
              >
                Ver mais
              </Button>
            )}

            {/*Botão "Ver menos" versão Mobile*/}
            {(visibleLeads > 12 && isMobile) && (
              <Button
                onClick={() => setVisibleLeads(12)}
                variant="outlined"
                size="small"
                sx={{
                  px: 0.5, py: 0.5,
                  border: '1px solid #e53935',
                  borderRadius: '100%',
                  background: 'rgba(229, 57, 53, 0.08)',
                  color: '#e53935',
                  fontWeight: 1000,
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  minWidth: '40px',
                  minHeight: '40px',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(229, 57, 53, 0.2)',
                    background: 'rgba(229, 57, 53, 0.12)',
                  }
                }}
              >
                —
              </Button>
            )}

            {/*Botão "Ver menos" versão Desktop*/}
            {(visibleLeads > 12 && !isMobile) && (
              <Button
                onClick={() => setVisibleLeads(12)}
                variant="outlined"
                size="small"
                sx={{
                  px: 2, py: 0.5,
                  border: '1px solid #e53935',
                  borderRadius: '16px',
                  background: 'rgba(229, 57, 53, 0.08)',
                  color: '#e53935',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  minWidth: '80px',
                  minHeight: '28px',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(229, 57, 53, 0.2)',
                    background: 'rgba(229, 57, 53, 0.12)',
                  },
                  '&:disabled': {
                    opacity: 0.6,
                  }
                }}
              >
                Ver menos
              </Button>
            )}

            {/* ⬅️ Botão "Ver todos" com loading */}
            {visibleLeads < leads.length && (
              <Button
                onClick={handleViewAll} // ⬅️ Mudança aqui para usar a nova função
                variant="outlined"
                size="small"
                disabled={loadingAll} // ⬅️ Desabilita durante o loading
                sx={{
                  px: 2, py: 0.5,
                  border: '1px solid #43a047',
                  borderRadius: '16px',
                  background: 'rgba(76, 175, 80, 0.08)',
                  color: '#43a047',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  minWidth: '80px',
                  minHeight: '28px',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.2)',
                    background: 'rgba(76, 175, 80, 0.12)',
                  },
                  '&:disabled': {
                    opacity: 0.6,
                  }
                }}
              >
                {loadingAll ? 'Carregando...' : 'Ver todos'} {/* ⬅️ Texto dinâmico */}
              </Button>
            )}
          </Box>

          {/* ⬅️ Indicador de quantidade - opcional */}
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              mt: 1,
              display: 'block',
              fontSize: '0.75rem'
            }}
          >
            Mostrando {Math.min(visibleLeads, leads.length)} de {leads.length} leads
          </Typography>
        </Box>
      )}
    </>
  );
};

export default LeadsTableSDRVirtual;
